import { ApplicationConfig, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer, ErrorHandler } from '@angular/core';
import { provideRouter, withNavigationErrorHandler, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { Router } from '@angular/router';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import * as Sentry from '@sentry/angular';

import { campaignReducer } from '@features/campaigns/store/campaign.reducer';
import { CampaignEffects } from '@features/campaigns/store/campaign.effects';

import { walletReducer } from './shared/store/wallets';
import { WalletEffects } from './shared/store/wallets/wallet.effects';

import { ProjectEffects } from './shared/store/projects';
import { projectsReducer } from './shared/store/projects';

import { routes } from './app.routes';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { httpErrorInterceptor } from '@core/interceptors/http.interceptor';

import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { environment } from '@environments/environment';

import { facebookSdkInitializer } from './core/initializer/facebook-initializer';

import { providePrimeNG } from 'primeng/config';
import { MediabyPreset } from '@styles/mediaby-preset';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { initialInterfaceLanguage } from '@core/i18n/interface-language';

registerLocaleData(localeFr, 'fr-FR');

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function getPrimeNGTranslation(lang: string) {
    const translations: Record<string, any> = {
        fr: {
            dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
            dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
            dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
            monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
            monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
            today: "Aujourd'hui",
            clear: 'Effacer',
            dateFormat: 'dd/mm/yy',
            firstDayOfWeek: 1,
            weak: 'Faible',
            medium: 'Moyen',
            strong: 'Fort',
            passwordPrompt: 'Entrez un mot de passe',
        },
        en: {
            dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
            monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
            monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            today: 'Today',
            clear: 'Clear',
            dateFormat: 'mm/dd/yy',
            firstDayOfWeek: 0,
            weak: 'Weak',
            medium: 'Medium',
            strong: 'Strong',
            passwordPrompt: 'Enter a password',
        },
        es: {
            dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
            dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
            dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
            monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
            monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
            today: 'Hoy',
            clear: 'Limpiar',
            dateFormat: 'dd/mm/yy',
            firstDayOfWeek: 1,
            weak: 'Débil',
            medium: 'Medio',
            strong: 'Fuerte',
            passwordPrompt: 'Introduce una contraseña',
        },
    };
    return translations[lang] || translations['fr'];
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            routes,
            withNavigationErrorHandler((error) => {
                const isChunkLoadError =
                    error instanceof TypeError &&
                    (error.message.includes('Failed to fetch dynamically imported module') ||
                        error.message.includes('Importing a module script failed'));
                if (isChunkLoadError) {
                    window.location.assign(window.location.href);
                }
            }),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
        ),
        provideHttpClient(withInterceptors([authInterceptor, httpErrorInterceptor])),
        importProvidersFrom(
            TranslateModule.forRoot({
                // defaultLanguage: 'fr',
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient],
                },
            }),
        ),
        provideStore({
            campaigns: campaignReducer,
            wallet: walletReducer,
            projects: projectsReducer,
        }),
        provideEffects([CampaignEffects, WalletEffects, ProjectEffects]),
        provideStoreDevtools({ maxAge: 25 }),
        { provide: LOCALE_ID, useValue: 'fr-FR' },
        importProvidersFrom(RecaptchaV3Module),
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.captcha_site_key_v3 },
        MessageService,
        provideAppInitializer(facebookSdkInitializer()),
        providePrimeNG({
            theme: {
                preset: MediabyPreset,
                options: {
                    darkModeSelector: false || 'none',
                    cssLayer: true,
                },
            },
            translation: getPrimeNGTranslation(initialInterfaceLanguage()),
        }),
        DialogService,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
    ],
};
